import { MenuOutlined, CloseOutlined } from "@ant-design/icons";
import { Drawer } from "antd";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HeaderLogo } from "../../assets/images/Icons";
import SettingsModal from "../../components/Settings/SettingsModal";
import logoSvg from "../../assets/images/logo-immosight.png";
import { useGetSubscriptionStatusQuery } from "../../services/Subscription";
import "./AppLayout.css";

const NAV_ITEMS = [
  {
    label: "Deals",
    path: "/properties",
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <rect x="1" y="1" width="5" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
        <rect x="8" y="1" width="5" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
        <rect x="1" y="8" width="5" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
        <rect x="8" y="8" width="5" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
      </svg>
    ),
  },
  {
    label: "Vergleich",
    path: "/comparison",
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <rect x="1" y="4" width="3" height="8" rx="1" stroke="currentColor" strokeWidth="1.2"/>
        <rect x="5.5" y="6" width="3" height="6" rx="1" stroke="currentColor" strokeWidth="1.2"/>
        <rect x="10" y="2" width="3" height="10" rx="1" stroke="currentColor" strokeWidth="1.2"/>
      </svg>
    ),
  },
];

const NAV_SECONDARY = [
  {
    label: "Kalkulation",
    action: "settings",
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="2.3" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M7 1v1.4M7 11.6V13M1 7h1.4M11.6 7H13M2.8 2.8l1 1M10.2 10.2l1 1M2.8 11.2l1-1M10.2 3.8l1-1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    label: "Abo & Abrechnung",
    path: "/plans-and-billing",
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M7 1L8.8 5.5H13.5L9.5 8L11 12.5L7 10L3 12.5L4.5 8L0.5 5.5H5.2L7 1Z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
      </svg>
    ),
  },
];

const AppLayout = ({ auth, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);

  const userName = auth?.user?.profile?.name || "User";
  const userEmail = auth?.user?.profile?.email || "";
  const userInitials = userName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

  const { data: subscriptionData } = useGetSubscriptionStatusQuery();
  // Use `plan` field (derived from Keycloak roles) — not `status`, which defaults to 'active'
  // for users without a Stripe record and would always show "Pro".
  const isPro = subscriptionData?.plan === 'pro';
  const planLabel = isPro ? 'Pro' : 'Free';

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  // Click-outside handler for user dropdown
  useEffect(() => {
    function handleClickOutside(e) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
    }
    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userDropdownOpen]);

  const handleNavClick = (item) => {
    if (item.action === "settings") {
      setIsSettingsModalOpen(true);
    } else if (item.path) {
      navigate(item.path);
    }
    setMobileOpen(false);
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="vl-sb-logo">
        <img src={logoSvg} alt="ImmoSight" style={{ height: 26, width: 'auto' }} />
      </div>

      {/* Primary nav */}
      <nav className="vl-sb-nav">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.label}
            className={`vl-nav-btn ${isActive(item.path) ? "active" : ""}`}
            onClick={() => handleNavClick(item)}
          >
            {item.icon}
            {item.label}
          </button>
        ))}

        <div className="vl-sb-sep" />

        {NAV_SECONDARY.map((item) => (
          <button
            key={item.label}
            className={`vl-nav-btn ${item.path && isActive(item.path) ? "active" : ""}`}
            onClick={() => handleNavClick(item)}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </nav>

      {/* User */}
      <div className="vl-sb-user" ref={userDropdownRef} style={{ position: 'relative' }}>

        {/* Upgrade-CTA — nur für Free-Nutzer */}
        {!isPro && (
          <>
            <button
              onClick={() => navigate('/plans-and-billing')}
              style={{
                width: '100%',
                marginBottom: 6,
                padding: '4px 8px',
                background: 'transparent',
                border: 'none',
                borderRadius: 0,
                textAlign: 'left',
                fontSize: 14,
                fontWeight: 500,
                lineHeight: '20px',
                color: '#1DB3B3',
                cursor: 'pointer',
                transition: 'background 150ms, color 150ms',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#E8F7F7';
                e.currentTarget.style.borderRadius = '6px';
                e.currentTarget.style.color = '#0E7A6B';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderRadius = '0';
                e.currentTarget.style.color = '#1DB3B3';
              }}
            >
              Auf Pro upgraden →
            </button>
            <div className="vl-sb-sep" />
          </>
        )}

        {/* User-Row als Dropdown-Trigger */}
        <button
          onClick={() => setUserDropdownOpen(prev => !prev)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            background: userDropdownOpen ? '#E8F7F7' : 'transparent',
            border: 'none',
            borderRadius: 8,
            padding: '6px 8px',
            cursor: 'pointer',
            transition: 'background 150ms',
            textAlign: 'left',
          }}
          onMouseEnter={e => { if (!userDropdownOpen) e.currentTarget.style.background = '#F3F3F5'; }}
          onMouseLeave={e => { if (!userDropdownOpen) e.currentTarget.style.background = 'transparent'; }}
        >
          <div className="vl-user-av">{userInitials}</div>
          <div className="vl-user-info" style={{ flex: 1, minWidth: 0 }}>
            <div className="vl-user-name">{userName}</div>
            <div
              className="vl-user-plan"
              style={{ color: isPro ? '#1FA534' : '#8C8FA0' }}
            >
              {planLabel}
            </div>
          </div>
          {/* Chevron — zeigt Richtung je nach Zustand */}
          <svg
            width="14" height="14" viewBox="0 0 14 14" fill="none"
            style={{
              flexShrink: 0,
              color: '#8C8FA0',
              transform: userDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease',
            }}
          >
            <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Dropdown — öffnet nach OBEN */}
        {userDropdownOpen && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              right: 0,
              marginBottom: 8,
              background: '#FFFFFF',
              borderRadius: 12,
              boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
              overflow: 'hidden',
              animation: 'userDropdownIn 150ms ease',
            }}
          >
            {/* Header: Name + E-Mail */}
            <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid #F0F0EF' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1F1F1F', lineHeight: '20px' }}>{userName}</div>
              <div style={{ fontSize: 12, color: '#8C8FA0', lineHeight: '18px', marginTop: 1 }}>{userEmail}</div>
            </div>

            {/* Profil verwalten */}
            <button
              onClick={() => {
                window.open(auth.settings?.authority + '/account', '_blank');
                setUserDropdownOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 16px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                fontSize: 14,
                color: '#1F1F1F',
                cursor: 'pointer',
                transition: 'background 150ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#E8F7F7'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              Profil verwalten
            </button>

            {/* Passwort ändern */}
            <button
              onClick={() => {
                window.open(auth.settings?.authority + '/account/security', '_blank');
                setUserDropdownOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 16px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                fontSize: 14,
                color: '#1F1F1F',
                cursor: 'pointer',
                transition: 'background 150ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#E8F7F7'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              Passwort ändern
            </button>

            {/* Trennlinie */}
            <div style={{ height: 1, background: '#F0F0EF', margin: '4px 0' }} />

            {/* Abmelden */}
            <button
              onClick={() => {
                setUserDropdownOpen(false);
                auth.signoutRedirect();
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 16px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                fontSize: 14,
                color: '#E83838',
                cursor: 'pointer',
                transition: 'background 150ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#FDEAEA'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              Abmelden
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="vl-app">
      {/* Desktop sidebar */}
      <aside className="vl-sidebar">
        {sidebarContent}
      </aside>

      {/* Mobile drawer */}
      <Drawer
        placement="left"
        closable={false}
        onClose={() => setMobileOpen(false)}
        open={mobileOpen}
        width={240}
        bodyStyle={{ padding: 0 }}
        className="vl-mobile-drawer"
      >
        {sidebarContent}
      </Drawer>

      {/* Main content */}
      <div className="vl-main">
        {/* Mobile-only topbar */}
        <div className="vl-topbar">
          <button className="vl-mobile-menu" onClick={() => setMobileOpen(true)}>
            <MenuOutlined />
          </button>
          <img src={logoSvg} alt="ImmoSight" style={{ height: 24, width: 'auto' }} />
          <div className="vl-tb-spacer" />
        </div>

        {/* Content */}
        <div className="vl-content">
          {children}
        </div>
      </div>

      <SettingsModal
        open={isSettingsModalOpen}
        onCancel={() => setIsSettingsModalOpen(false)}
        userID={auth?.user?.profile?.sub}
        userName={auth?.user?.profile?.name}
        userEmail={auth?.user?.profile?.email}
      />
    </div>
  );
};

export default AppLayout;
